import { ESupportedGames } from "../../@types";
import { IAllstarUser } from "../../@types/user";
import { TPlayer } from "./@types";

const { REACT_APP_S3_CLIP_BUCKET } = process.env;

export const getMapKey = (hero: string | undefined): string | undefined => {
  if (!hero) return undefined;
  const filename = hero.split("/").pop()?.split(".")[0] || "";
  return filename.replace(/_\d+$/, "") || undefined;
};

// Pre-blurred map previews for the Match VOD upsell:
// media.allstar.gg/static/web/preview/<map>/<1-50>.jpg
// Flip to true once the bucket is populated; until then the upsell falls back
// to blurring the match hero image in CSS.
export const MAP_PREVIEW_ENABLED = true;

// Every map ships exactly this many frames. Keep in sync with --count in the
// available, so all maps are levelled to 90 rather than the frontend carrying
// a per-map count. Keep in sync with --count in the allstar-map-previews repo.
const MAP_PREVIEW_COUNT = 50;

// Maps we generated previews for. Anything else (Office, Italy, community
// maps, other games) borrows from this pool: heavily blurred at 320px they
// read as generic Counter-Strike, which beats a flat dark box. Picking from
// several keeps unknown maps from all looking identical.
const FALLBACK_MAPS = [
  "de_dust2",
  "de_train",
  "de_inferno",
  "de_mirage",
  "de_nuke",
  "de_cache",
];
const MAPS_WITH_PREVIEWS = [
  "de_dust2",
  "de_mirage",
  "de_inferno",
  "de_ancient",
  "de_anubis",
  "de_nuke",
  "de_vertigo",
  "de_overpass",
  "de_train",
  "de_cache",
];

// Seeded off the match id rather than Math.random so a given match always
// shows the same frame. Re-randomising on every render would flicker while
// scrolling and would break the illusion that it is their footage.
const seedToIndex = (seed: string, count: number): number => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  return (Math.abs(hash) % count) + 1;
};

export const getMapPreviewImage = (
  hero: string | undefined,
  seed: string | undefined,
): string | undefined => {
  if (!MAP_PREVIEW_ENABLED || !seed) return undefined;

  const mapKey = getMapKey(hero);
  // Salted so the fallback map and the frame index are picked independently;
  // still seeded off the match id, so it never changes between renders.
  const folder =
    mapKey && MAPS_WITH_PREVIEWS.includes(mapKey)
      ? mapKey
      : FALLBACK_MAPS[seedToIndex(`${seed}:map`, FALLBACK_MAPS.length) - 1];
  return `${REACT_APP_S3_CLIP_BUCKET}/static/web/previews/${folder}/${seedToIndex(
    seed,
    MAP_PREVIEW_COUNT,
  )}.jpg`;
};

export const getMapIconUrl = (hero: string | undefined): string | undefined => {
  const mapKey = getMapKey(hero);
  if (!mapKey) return undefined;
  return `${REACT_APP_S3_CLIP_BUCKET}/static/web/cs2_icons/map_icon/${mapKey}.png`;
};

const parseDate = (date: string) =>
  /^\d+$/.test(date) ? new Date(parseInt(date)) : new Date(date);

export const formatDateRelative = (date: string) => {
  const parsed = parseDate(date);
  const now = new Date();
  const diffMs = now.getTime() - parsed.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return parsed.toLocaleString("en-us", { month: "short", day: "numeric" });
};

export const formatDateFull = (date: string) => {
  const parsed = parseDate(date);
  const now = new Date();
  const diffMs = now.getTime() - parsed.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const sameYear = parsed.getFullYear() === now.getFullYear();

  if (diffMins < 60)
    return `${diffMins} minute${diffMins !== 1 ? "s" : ""} ago`;
  if (diffHours < 24)
    return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
  if (sameYear) {
    return parsed.toLocaleString("en-us", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }
  return parsed.toLocaleString("en-us", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const formatDate = (date: string) => {
  return parseDate(date).toLocaleString("en-us", {
    month: "short",
    day: "numeric",
  });
};
const WEAPON_SHORT_NAMES: Record<string, string> = {
  "Desert Eagle": "Deagle",
  "Dual Berettas": "Dualies",
  "Five-SeveN": "Five-7",
  "Sawed-Off": "Sawed-Off",
  "PP-Bizon": "Bizon",
};

export const shortenMomentTitle = (title: string) => {
  let result = title;
  for (const [full, short] of Object.entries(WEAPON_SHORT_NAMES)) {
    result = result.replace(full, short);
  }
  return result;
};

export const formatTime = (time: number) => {
  const date = new Date(time * 1000).toISOString();
  return time > 3600 ? date.substring(11, 19) : date.substring(14, 19);
};
export const formatGameMode = (gameMode: string) =>
  `${gameMode.charAt(0)}${gameMode.slice(1).toLowerCase()}`;
export const formatDuration = (seconds: number) =>
  `${~~(seconds / 60)}:${seconds % 60}`;
export const mustLinkAnAccount = ({
  game,
  allstarUser,
}: {
  game: ESupportedGames;
  allstarUser: IAllstarUser;
}) => {
  switch (game) {
    case ESupportedGames.VALORANT:
    case ESupportedGames.LOL:
      return !allstarUser?.user?.riot?.active;
    case ESupportedGames.DOTA2:
    case ESupportedGames.CS2:
      return (
        !allstarUser?.user?.steam?.active &&
        !allstarUser?.user?.faceit?.nickname
      );
    case ESupportedGames.FORTNITE:
      return !allstarUser?.user?.epic?.id;
    case ESupportedGames.OW2:
      return !allstarUser?.user?.battlenet?.battletag;
    case ESupportedGames.NONE:
      return true;

    default:
      return null;
  }
};
export const getPlayerSearchIdentifier = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get("playerSearchIdentifier") || "";
};
export const getPlayerGameIdByGame = (
  game: ESupportedGames | undefined,
  user: IAllstarUser,
): string | null => {
  if (!game) return null;
  switch (game) {
    case ESupportedGames.DOTA2:
    case ESupportedGames.CS2:
      return user?.user?.steam?.steamid ?? null;
    case ESupportedGames.LOL:
      return user?.user?.riot?.puuid ?? null;
    case ESupportedGames.FORTNITE:
      return user?.user?.epic?.id ?? null;
    case ESupportedGames.OW2:
      return user?.user?.battlenet?.battletag ?? null;
    case ESupportedGames.VALORANT:
      return user?.user?.riot?.riotId ?? null;
    default:
      return null;
  }
};

export const getMatchHistoryGameEnum = (game?: string | undefined) => {
  if (!game) return ESupportedGames.NONE;
  switch (game) {
    case "COUNTER_STRIKE_2":
    case "CS2":
      return ESupportedGames.CS2;
    case "COUNTER_STRIKE_GO":
      return ESupportedGames.CSGO;
    case "FORTNITE":
      return ESupportedGames.FORTNITE;
    case "DOTA2":
      return ESupportedGames.DOTA2;
    case "LEAGUE_OF_LEGENDS":
    case "LOL":
      return ESupportedGames.LOL;
    case "OVERWATCH_2":
    case "OW2":
      return ESupportedGames.OW2;
  }
};

export const getTextColorByMatchResult = (result: string) => {
  switch (result) {
    case "Win":
      return "envy";
    case "Victory":
      return "envy";
    case "Tie":
      return "error";
    case "Loss":
      return "alert";
    case "Defeat":
      return "alert";
  }
};

// Quentin currently rejects `moments` on the matches connection, so the
// multikill badge and Highlights column are held back. Flip to true and
// re-add the `moments(limit:)` selection to MATCHES_WITH_FILTERS (plus the
// $momentsLimit variable in MatchesViewmodel) to switch them on.
export const MOMENT_PREVIEW_ENABLED = false;
